import { useEffect, useState } from 'react';

/**
 * Viewport detection (CLAUDE.md §7).
 *
 * Phone vs tablet decision uses a composite condition (not just width)
 * so iPhone landscape (e.g. 926×428) doesn't accidentally trip into the
 * tablet two-pane layout — that would create a wrong mental model
 * for users lying in bed with their phone rotated.
 *
 * Returns 'phone' as a safe default during SSR-like contexts (window absent).
 *
 * Resize is debounced to 200ms to avoid thrashing on iPad Split View
 * transitions or on-screen-keyboard show/hide events.
 */

export type Viewport = 'phone' | 'tablet';

const TABLET_MIN_WIDTH = 820;
const TABLET_MAX_ASPECT = 1.7; // wider than this = phone landscape
const TABLET_MIN_HEIGHT = 700;

export function detectViewport(): Viewport {
  if (typeof window === 'undefined') return 'phone';
  const w = window.innerWidth;
  const h = window.innerHeight;
  const aspect = h > 0 ? w / h : 1;
  if (w >= TABLET_MIN_WIDTH && aspect < TABLET_MAX_ASPECT && h >= TABLET_MIN_HEIGHT) {
    return 'tablet';
  }
  return 'phone';
}

export function useViewport(): Viewport {
  const [viewport, setViewport] = useState<Viewport>(() => detectViewport());

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    function handler() {
      if (timeout !== undefined) clearTimeout(timeout);
      timeout = setTimeout(() => {
        setViewport(detectViewport());
      }, 200);
    }

    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('resize', handler);
      if (timeout !== undefined) clearTimeout(timeout);
    };
  }, []);

  return viewport;
}
